import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getMyTableOrder, type OrderDto } from '../lib/api.js'
import { useSessionStore } from '../stores/session.store.js'
import { formatMoney } from '../lib/money.js'
import { NintaiGOHeader } from '../components/NintaiGOHeader.js'
import { GuestActions } from '../components/GuestActions.js'

/**
 * OhSir — cuenta de la MESA (ítem D del backlog).
 *
 * El comensal que ordenó por el QR de mesa (DINE_IN) llega aquí a ver la cuenta
 * de su mesa e INICIAR el pago. La cuenta se resuelve desde el `tableSessionId`
 * del token guest (`getMyTableOrder`) — no hace falta un orderId.
 *
 * Sesión compartida: una mesa puede tener varios comensales (ítem A — sesiones
 * con código de unión) pero comparten UNA cuenta. Aquí se paga la cuenta
 * COMPLETA de la mesa — es lo simple y coherente: cualquiera de los comensales
 * inicia el pago del total y, al confirmarse, la mesa queda cerrada. (El
 * split/pago por comensal queda como mejora futura — reportado.)
 *
 * El botón "Pagar mi cuenta" lleva a la página Payment, reusando el patrón del
 * ítem C (takeout): el camino que funciona E2E es "pagar en caja" y el staff
 * confirma. El pago digital queda pendiente del procesador.
 */
export function Cuenta() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const session = useSessionStore((s) => s.session)
  const [order, setOrder] = useState<OrderDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [noBill, setNoBill] = useState(false)

  useEffect(() => {
    if (!session) {
      navigate(`/r/${slug}`, { replace: true })
      return
    }
    let alive = true
    void (async () => {
      const r = await getMyTableOrder(session.token)
      if (!alive) return
      setLoading(false)
      if (r.ok) {
        setOrder(r.data)
        setError(null)
        setNoBill(false)
      } else if (r.error.code === 'NOT_FOUND') {
        // La mesa aún no tiene cuenta — nadie ha enviado un pedido a cocina.
        setNoBill(true)
        setError(null)
      } else {
        setError(`${r.error.code}: ${r.error.message}`)
      }
    })()
    return () => {
      alive = false
    }
  }, [session, slug, navigate])

  if (!session) return <></>

  const alreadyPaid = order?.status === 'CLOSED'

  return (
    <div className="app">
      <NintaiGOHeader slug={slug} tableCode={session.tableCode} customerName={session.customerName} branchId={session.branchId} />
      <div className="container">
        <button
          className="btn btn-ghost"
          style={{ width: 'auto', marginBottom: 12 }}
          onClick={() => navigate(`/r/${slug}/menu`)}
        >
          ← Volver al menú
        </button>

        {error && (
          <div className="card" style={{ borderColor: 'var(--danger)' }}>
            <strong className="danger">No se pudo cargar tu cuenta</strong>
            <div className="muted">{error}</div>
          </div>
        )}

        {loading && (
          <div className="card">
            <span className="spinner" /> Cargando la cuenta de tu mesa…
          </div>
        )}

        {!loading && noBill && (
          <div className="card">
            <h2 style={{ marginTop: 0 }}>Tu mesa aún no tiene cuenta</h2>
            <p className="muted">
              En cuanto envíes un pedido a cocina, aquí verás la cuenta de tu
              mesa y podrás pagarla.
            </p>
            <button className="btn btn-primary" onClick={() => navigate(`/r/${slug}/menu`)}>
              Ver el menú
            </button>
          </div>
        )}

        {!loading && order && (
          <>
            <div className="card">
              <span className={`status-pill ${alreadyPaid ? 'ready' : ''}`}>
                {alreadyPaid ? 'Cuenta pagada' : 'Cuenta de la mesa'}
              </span>
              <h2 style={{ marginTop: 12, marginBottom: 4 }}>
                {alreadyPaid ? '¡Gracias por tu visita!' : 'Cuenta de tu mesa'}
              </h2>
              <p className="muted" style={{ marginTop: 0 }}>
                {alreadyPaid
                  ? 'Esta cuenta ya fue pagada y cerrada.'
                  : 'Esta es la cuenta completa de tu mesa, con todo lo que han ordenado.'}
              </p>
              <div style={{ marginTop: 12 }}>
                {order.items.map((it) => (
                  <div
                    key={it.id}
                    className="row"
                    style={{ borderTop: '1px solid var(--line)', paddingTop: 10, marginTop: 10 }}
                  >
                    <div className="grow">
                      <div style={{ fontWeight: 600 }}>
                        {(it.productName ?? it.name) ?? 'Producto'} × {it.quantity}
                      </div>
                      {it.modifiers && it.modifiers.length > 0 && (
                        <div className="muted" style={{ marginTop: 2 }}>
                          {it.modifiers.map((m) => m.name).join(' · ')}
                        </div>
                      )}
                      {it.notes && <div className="muted">{it.notes}</div>}
                    </div>
                    <div>
                      {formatMoney(
                        (it.unitPriceCents +
                          (it.modifiers ?? []).reduce((s, m) => s + m.priceDeltaCents, 0)) *
                          it.quantity,
                      )}
                    </div>
                  </div>
                ))}
                <div
                  className="row"
                  style={{ borderTop: '1px solid var(--line)', paddingTop: 12, marginTop: 12 }}
                >
                  <div className="grow"><strong>Total</strong></div>
                  <div style={{ color: 'var(--accent-2)', fontWeight: 700, fontSize: 18 }}>
                    {formatMoney(order.totalCents)}
                  </div>
                </div>
              </div>
            </div>

            {!alreadyPaid && (
              <>
                <button
                  className="btn btn-primary"
                  style={{ marginTop: 12 }}
                  onClick={() => navigate(`/r/${slug}/pago/${order.id}`)}
                >
                  Pagar mi cuenta
                </button>
                <p className="muted" style={{ textAlign: 'center', marginTop: 12 }}>
                  Es la cuenta completa de la mesa. Si son varios, uno paga el
                  total — o avísale al mesero.
                </p>
              </>
            )}
          </>
        )}
      </div>
      <GuestActions />
    </div>
  )
}
